import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Folder, ChevronLeft, Check, X, Image as ImageIcon } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL;
var IMAGE_RE = /\.(jpe?g|png|webp|gif|heic)$/i;
var START_PATH = '/Product Images';
var PAGE = 36;  // keep concurrent thumbnail requests low

function token() {
  return localStorage.getItem('admin_token') || '';
}

function headers() {
  var t = token();
  return t ? { Authorization: 'Bearer ' + t } : {};
}

function thumbUrl(path) {
  return API + '/api/admin/dropbox/thumbnail?path=' + encodeURIComponent(path) +
    '&t=' + encodeURIComponent(token());
}

// Browse the owner's Dropbox and attach images that are already there —
// no upload, no re-encoding, no edge timeouts.
export default function DropboxImagePicker(props) {
  var slug = props.slug;
  var [path, setPath] = useState(START_PATH);
  var [entries, setEntries] = useState([]);
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState('');
  var [picked, setPicked] = useState([]);
  var [saving, setSaving] = useState(false);
  var [makePrimary, setMakePrimary] = useState(false);
  var [query, setQuery] = useState('');
  var [limit, setLimit] = useState(PAGE);

  var browse = useCallback(async function(target) {
    setLoading(true); setError('');
    try {
      var r = await fetch(API + '/api/admin/dropbox/browse?path=' + encodeURIComponent(target),
        { headers: headers() });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Dropbox listing failed');
      setEntries(d.entries || []);
      setPath(target);
      setLimit(PAGE);
    } catch (e) {
      setEntries([]);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function() { browse(START_PATH); }, [browse]);

  function toggle(p) {
    setPicked(function(prev) {
      return prev.indexOf(p) === -1 ? prev.concat([p]) : prev.filter(function(x) { return x !== p; });
    });
  }

  function goUp() {
    var parts = path.split('/').filter(Boolean);
    parts.pop();
    browse(parts.length ? '/' + parts.join('/') : '');
  }

  async function attach() {
    if (!slug) { setError('Select a product first'); return; }
    if (!picked.length) { setError('Pick at least one image'); return; }
    setSaving(true); setError('');
    try {
      var r = await fetch(API + '/api/admin/product-images/' + encodeURIComponent(slug) + '/add-dropbox', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, headers()),
        body: JSON.stringify({ paths: picked, set_primary: makePrimary }),
      });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Could not attach those images');
      setPicked([]);
      setMakePrimary(false);
      if (props.onAttached) props.onAttached(d);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  var folders = entries.filter(function(e) { return e.type === 'folder'; });
  var images = entries.filter(function(e) { return e.type === 'file' && IMAGE_RE.test(e.name); });
  if (query) {
    var q = query.toLowerCase();
    folders = folders.filter(function(e) { return e.name.toLowerCase().indexOf(q) !== -1; });
    images = images.filter(function(e) { return e.name.toLowerCase().indexOf(q) !== -1; });
  }
  var total = images.length;
  var shown = images.slice(0, limit);

  return React.createElement('div', {
    className: 'bg-neutral-950 border border-white/10 rounded-xl p-3 mb-3',
    'data-testid': 'dropbox-image-picker'
  },
    React.createElement('div', { className: 'flex flex-wrap items-center gap-2 mb-3' },
      React.createElement('button', {
        type: 'button',
        onClick: goUp,
        disabled: !path,
        'data-testid': 'dropbox-picker-up',
        className: 'flex items-center gap-1 text-neutral-300 hover:text-white text-xs border border-white/15 rounded-full px-3 py-1.5 disabled:opacity-30'
      }, React.createElement(ChevronLeft, { className: 'w-3.5 h-3.5' }), 'Up'),
      React.createElement('span', {
        className: 'text-neutral-500 text-xs font-mono truncate max-w-[55%]',
        'data-testid': 'dropbox-picker-path'
      }, path || '/ (Dropbox root)'),
      React.createElement('input', {
        value: query,
        onChange: function(e) { setQuery(e.target.value); setLimit(PAGE); },
        placeholder: 'Filter this folder…',
        'data-testid': 'dropbox-picker-filter',
        className: 'ml-auto bg-black border border-white/15 rounded-full px-3 py-1.5 text-xs text-white w-40 focus:outline-none focus:border-[#d4af37]'
      }),
      React.createElement('button', {
        type: 'button',
        onClick: function() { if (props.onClose) props.onClose(); },
        'data-testid': 'dropbox-picker-close',
        className: 'text-neutral-500 hover:text-white'
      }, React.createElement(X, { className: 'w-4 h-4' }))
    ),

    loading ? React.createElement('div', { className: 'py-10 text-center text-neutral-500 text-sm' },
      React.createElement(Loader2, { className: 'w-5 h-5 animate-spin mx-auto mb-2' }), 'Reading Dropbox…'
    ) : null,

    !loading && folders.length ? React.createElement('div', { className: 'flex flex-wrap gap-2 mb-3' },
      folders.map(function(f) {
        return React.createElement('button', {
          key: f.path,
          type: 'button',
          onClick: function() { setQuery(''); browse(f.path); },
          'data-testid': 'dropbox-folder-' + f.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          className: 'flex items-center gap-1.5 bg-black border border-white/10 hover:border-[#d4af37]/60 rounded-lg px-3 py-2 text-xs text-neutral-300'
        }, React.createElement(Folder, { className: 'w-3.5 h-3.5 text-[#d4af37]' }), f.name);
      })
    ) : null,

    !loading && images.length ? React.createElement('div', { className: 'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2' },
      shown.map(function(f) {
        var on = picked.indexOf(f.path) !== -1;
        return React.createElement('button', {
          key: f.path,
          type: 'button',
          onClick: function() { toggle(f.path); },
          title: f.name,
          'data-testid': 'dropbox-image-option',
          className: 'relative rounded-lg overflow-hidden border-2 transition-colors ' + (on ? 'border-[#d4af37]' : 'border-white/10 hover:border-white/30')
        },
          React.createElement('img', {
            src: thumbUrl(f.path),
            alt: f.name,
            className: 'w-full aspect-square object-cover bg-black/40'
          }),
          React.createElement('div', { className: 'absolute inset-x-0 bottom-0 bg-black/85 px-1.5 py-1 text-[10px] text-neutral-300 truncate' }, f.name),
          on ? React.createElement('div', { className: 'absolute top-1 right-1 w-5 h-5 bg-[#d4af37] rounded-full flex items-center justify-center' },
            React.createElement(Check, { className: 'w-3 h-3 text-black' })
          ) : null
        );
      })
    ) : null,

    !loading && !images.length && !folders.length ? React.createElement('p', {
      className: 'py-8 text-center text-neutral-600 text-sm'
    }, React.createElement(ImageIcon, { className: 'w-5 h-5 mx-auto mb-2' }), 'Nothing here — try going up a level') : null,

    !loading && total > shown.length ? React.createElement('button', {
      type: 'button',
      onClick: function() { setLimit(limit + PAGE); },
      'data-testid': 'dropbox-picker-more',
      className: 'mt-2 w-full text-xs text-neutral-300 hover:text-white border border-white/15 rounded-full py-2'
    }, 'Show more (' + shown.length + ' of ' + total + ')') : null,

    React.createElement('label', { className: 'flex items-center gap-2 mt-3 text-neutral-400 text-sm cursor-pointer select-none' },
      React.createElement('input', {
        type: 'checkbox',
        checked: makePrimary,
        onChange: function(e) { setMakePrimary(e.target.checked); },
        className: 'w-4 h-4 accent-[#d4af37]'
      }),
      'Make the first picked image the primary (cover) image'
    ),

    React.createElement('button', {
      type: 'button',
      onClick: attach,
      disabled: saving || !picked.length,
      'data-testid': 'dropbox-picker-attach',
      className: 'mt-2 w-full flex items-center justify-center gap-2 bg-[#d4af37] text-black font-semibold py-2.5 rounded-full hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
    },
      saving ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : null,
      saving ? 'Attaching…' : (picked.length ? 'Attach ' + picked.length + ' image' + (picked.length === 1 ? '' : 's') : 'Pick images above')
    ),

    error ? React.createElement('div', {
      'data-testid': 'dropbox-picker-error',
      className: 'mt-2 px-3 py-2 rounded-lg text-sm bg-red-500/10 border border-red-500/30 text-red-300'
    }, '⚠️ ' + error) : null
  );
}
